import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { listCategories, listFacets, listProducts } from '@/api/products';
import { toStars } from '@/utils/format';

const CatalogContext = createContext(null);

/**
 * Loads products + categories once and decorates each product with the
 * presentation fields the prototype's deco() derived (tint, deep, letter, stars).
 */
export function CatalogProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [facets, setFacets] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      listProducts({ signal: controller.signal }),
      listCategories({ signal: controller.signal }),
      listFacets({ signal: controller.signal }),
    ])
      .then(([nextProducts, nextCategories, nextFacets]) => {
        setProducts(nextProducts);
        setCategories(nextCategories);
        setFacets(nextFacets);
        setStatus('ready');
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setError(err);
        setStatus('error');
      });

    return () => controller.abort();
  }, []);

  const value = useMemo(() => {
    const byCatId = new Map(categories.map((c) => [c.id, c]));

    const decorate = (product) => {
      const category = byCatId.get(product.cat);
      return {
        ...product,
        categoryId: product.cat,
        categoryName: category?.name ?? product.cat,
        tint: category?.tint ?? '#eee',
        deep: category?.deep ?? '#999',
        letter: (product.name ?? '?').charAt(0) || '?',
        stars: toStars(product.rating),
        ratingCount: product.rc,
        // Books carry no ratings, so the UI hides the stars rather than showing
        // an empty five-star row on every listing.
        hasRating: product.rc > 0,
      };
    };

    const decorated = products.map(decorate);

    return {
      status,
      error,
      categories: categories.map((c) => ({
        ...c,
        count: products.filter((p) => p.cat === c.id).length,
      })),
      products: decorated,
      facets,
      /** Ids are strings from the API (Mongo ObjectIds) and numbers in the
       *  mock data, and route params always arrive as strings — compare as strings. */
      getProduct: (id) => decorated.find((p) => String(p.id) === String(id)),
      /** Most-rated four, as the prototype's "Fresh picks" did. With unrated
       *  books this falls back to the newest four, which is the catalog's order. */
      featured: [...decorated].sort((a, b) => b.rc - a.rc).slice(0, 4),
    };
  }, [products, categories, facets, status, error]);

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error('useCatalog must be used inside <CatalogProvider>');
  return ctx;
}
